/**
 * Casse d'affichage d'une commune.
 *
 * Le catalogue (real-projects.json) porte une partie des communes en
 * capitales (« LA BOISSIERE-DES-LANDES », 185 fiches au 08/10/2026). On ne
 * corrige pas le fichier : la casse est rétablie à l'AFFICHAGE seulement.
 *
 * Règle : chaque mot prend une capitale initiale ; les articles et
 * prépositions (« de », « des », « en », « sur »…) restent en minuscules
 * sauf en tête ; « d' » et « l' » s'élident et le mot qui suit prend sa
 * capitale (« Ombree d'Anjou », « Saint-Michel-en-l'Herm ») ; les traits
 * d'union et les espaces sont conservés. Les accents ne se devinent pas :
 * « Boissiere » reste sans accent.
 *
 * Une commune déjà écrite en casse mixte est rendue telle quelle.
 *
 * Module sans dépendance : utilisable côté serveur comme dans un composant
 * navigateur.
 */

const PETITS_MOTS = new Set(['en', 'sur', 'sous', 'de', 'des', 'du', 'la', 'le', 'les', 'lès', 'lez', 'aux', 'au', 'et'])

const majuscule = (m: string): string => m.charAt(0).toUpperCase() + m.slice(1)

/** Un mot (sans espace ni trait d'union), déjà en minuscules. */
function mot(m: string, enTete: boolean): string {
  if (!m) return m
  // Élision : « d'anjou » → « d'Anjou », « l'herm » → « l'Herm » (« L'Isle » en tête)
  const elision = m.match(/^([dl])(['’])(.+)$/)
  if (elision) {
    const [, lettre, apostrophe, reste] = elision
    return `${enTete ? lettre!.toUpperCase() : lettre}${apostrophe}${majuscule(reste!)}`
  }
  if (!enTete && PETITS_MOTS.has(m)) return m
  // « vald'yerre » → « Vald'Yerre » ; « gommenec'h » reste « Gommenec'h » (c'h breton)
  return majuscule(m).replace(/([dl])(['’])(\p{L})/gu, (_, l: string, a: string, c: string) => `${l}${a}${c.toUpperCase()}`)
}

export function communeAffichee(c: string): string
export function communeAffichee(c: string | null | undefined): string | null | undefined
export function communeAffichee(c: string | null | undefined): string | null | undefined {
  if (!c) return c
  // Casse mixte : écrite à la main, on n'y touche pas
  if (c !== c.toUpperCase()) return c
  const morceaux = c.toLowerCase().split(/([\s-]+)/)
  return morceaux.map((m, i) => (i % 2 === 1 ? m : mot(m, i === 0))).join('')
}

/** « à Souppes-sur-Loing », « au Mans », « aux Sables d'Olonne ». */
export function aLaCommune(c: string): string {
  if (/^Le\s/.test(c)) return `au ${c.slice(3)}`
  if (/^Les\s/.test(c)) return `aux ${c.slice(4)}`
  return `à ${c}`
}

/** « de Souppes-sur-Loing », « d’Escorneboeuf », « du Mans », « des Sables d'Olonne ». */
export function deLaCommune(c: string): string {
  if (/^Le\s/.test(c)) return `du ${c.slice(3)}`
  if (/^Les\s/.test(c)) return `des ${c.slice(4)}`
  if (/^[aeiouyàâäéèêëîïôöûüœ]/i.test(c)) return `d’${c}`
  return `de ${c}`
}
